import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Image, PlaySolid, rem, Text } from '@bonusly/design-system';

import { Video } from '../../types/announcements';

interface StackCardContentProps {
	title: string;
	description: string;
	imageUrl?: string;
	video?: Video | null;
	hasIncentive?: boolean;
	isFocused?: boolean;
	isTopCard?: boolean;
	isExpanded?: boolean;
}

/**
 * Simplified card content for the announcements stack.
 * Shows only title, description, optional image or video thumbnail, and incentive link.
 */
export const StackCardContent: FC<StackCardContentProps> = ({
	title,
	description,
	imageUrl,
	video,
	hasIncentive,
	isFocused = false,
	isTopCard = false,
	isExpanded = false,
}) => {
	const { t } = useTranslation('announcements');

	const videoThumbSrc = video?.video_thumbnail_url || video?.video_poster_url;
	const showVideo = !!video;
	const showImage = !showVideo && !!imageUrl;

	return (
		<Box
			bg="bg.surface"
			px={4}
			py={3}
			borderRadius="md"
			border="1px solid"
			borderColor={isFocused ? 'primary.fg' : 'border.default'}
			boxShadow={isFocused ? 'outline' : 'xs'}
			width="100%"
			textAlign="left"
			display="flex"
			minH={24}
			alignItems="center"
			gap={6}
			transition="border-color 100ms ease, box-shadow 100ms ease"
			_hover={isExpanded ? { borderColor: 'border.strong' } : undefined}
			_groupHover={isTopCard && !isFocused ? { borderColor: 'border.strong' } : undefined}
		>
			<Box flex={1} display="flex" flexDirection="column" gap={0.5} minW={0}>
				<Text fontSize={16} fontWeight="semibold" noOfLines={1}>
					{title}
				</Text>
				<Text
					fontSize={14}
					color="text.soft"
					noOfLines={hasIncentive ? 1 : 2}
					sx={{
						'& *': {
							fontSize: 'inherit',
							fontWeight: 'normal',
							fontStyle: 'normal',
							color: 'inherit',
							margin: 0,
							padding: 0,
							display: 'inline',
						},
						'& p + p::before': {
							content: '" "',
						},
					}}
					dangerouslySetInnerHTML={{ __html: description }}
				/>
				{hasIncentive && (
					<Text as="span" color="text.interactive" fontSize="sm" fontWeight="medium">
						{t('carousel.incentive_button')}
					</Text>
				)}
			</Box>

			{/* Always reserve space for the media thumbnail so every card in the stack has the same height */}
			<Box position="relative" w={24} h={16} flexShrink={0} bg={showImage || showVideo ? 'bg.subtle' : undefined} borderRadius="md" overflow="hidden">
				{showImage && <Image src={imageUrl} alt="" objectFit="cover" w="100%" h="100%" position="absolute" inset={0} />}
				{showVideo && videoThumbSrc && (
					<Image src={videoThumbSrc} alt={t('carousel.video_modal_video_alt')} objectFit="cover" w="100%" h="100%" position="absolute" inset={0} />
				)}
				{/* video_thumbnail_url already has a play button rendered in; only overlay our own
				    when we're falling back to the plain poster or the no-image processing state. */}
				{showVideo && !video?.video_thumbnail_url && (
					<Flex position="absolute" inset={0} alignItems="center" justifyContent="center" pointerEvents="none">
						<Flex alignItems="center" justifyContent="center" w={rem(28)} h={rem(28)} borderRadius="full" backgroundColor="blackAlpha.700" color="text.onColor">
							<PlaySolid size="xs" />
						</Flex>
					</Flex>
				)}
			</Box>
		</Box>
	);
};
